import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AllRoles from '../../components/AllRoles/AllRoles'
import Pagination from '@/shared/components/Pagination/Pagination'
import { useRoles, useToggleRoleActive } from '@/features/role'
import { Role } from '../../types'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog/ConfirmDialog'
import ActionHeader from '@/shared/components/ActionHeader/ActionHeader'
import { Separator } from '@/ui/separator'
import { Loader } from '@/shared/components/Loader/Loader'
import styles from '../RolePage/Rolepage.module.scss'
import { toast } from 'sonner'

export default function RoleActivePage() {
  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pageSize, setPageSize] = useState(10)

  const {
    data: activeRoles,
    isLoading: isActiveLoading,
    isError: isActiveError,
    refetch: refetchActive,
  } = useRoles({ is_active: true })

  const toggleRoleActiveMutation = useToggleRoleActive()

  const activeFiltered = (activeRoles ?? []).filter((r: Role) => {
    const name = r?.name || ''
    return name.toLowerCase().includes(search.toLowerCase())
  })

  const activeTotalPages = Math.max(1, Math.ceil(activeFiltered.length / pageSize))
  const activeDisplayedRoles = activeFiltered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  const handleEdit = (role: Role) => {
    navigate(`/roles/${role.id}/edit`)
  }

  const handleDelete = (role: Role) => {
    if (role.type === 'Default') {
      setSelectedRole(null)
      setConfirmOpen(true)
      return
    }
    setSelectedRole(role)
    setConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (!selectedRole) return
    toggleRoleActiveMutation.mutate(selectedRole.id, {
      onSuccess: () => {
        toast.success('Role deleted successfully')
      },
      onError: (error: any) => {
        const apiMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Unable to delete role'
        toast.error(apiMessage)
      },
    })
    setConfirmOpen(false)
    setSelectedRole(null)
  }

  return (
    <div>
      {isActiveLoading ? (
        <Loader text="Loading active roles..." />
      ) : isActiveError ? (
        <p className={styles.error}>Error loading active roles</p>
      ) : (
        <>
          <div className={styles.actionHeaderWrapper}>
            <ActionHeader
              title="Active Roles"
              search={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search Roles"
              actions={[
                {
                  icon: 'refresh',
                  onClick: () => refetchActive(),
                  variant: 'icon',
                  ariaLabel: 'Refresh',
                  key: 'refresh-roles',
                },
                {
                  icon: 'add',
                  label: 'Create Role',
                  onClick: () => navigate('/roles/create'),
                  variant: 'primary',
                  key: 'create-role',
                },
              ]}
            />
          </div>
          <Separator />
          <div className={styles.tableWrapper}>
            <AllRoles
              mode="active"
              roles={activeDisplayedRoles}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
            {activeFiltered.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={activeTotalPages}
                onPageChange={setCurrentPage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(size) => {
                  setPageSize(size)
                  setCurrentPage(1)
                }}
              />
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={
          selectedRole?.type === 'Default'
            ? 'Cannot delete default role'
            : 'Delete role'
        }
        message={
          selectedRole?.type === 'Default'
            ? 'Default roles cannot be deleted. Please choose a custom role to remove.'
            : `Delete role ${selectedRole?.name}? This will mark it as inactive.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={
          selectedRole?.type === 'Default'
            ? () => setConfirmOpen(false)
            : handleConfirmDelete
        }
        onCancel={() => {
          setConfirmOpen(false)
          setSelectedRole(null)
        }}
      />
    </div>
  )
}
